import type { MouseEvent, VoidFunctionComponent } from 'react';
import { useMemo } from 'react';
import { Button } from 'react-bootstrap';
import * as _ from 'lodash';
import ProjectAffiliationContainer from '../generic/ProjectAffiliation';
import { ActionCards } from '../overview/StandardProjectSummaryActionCards';
import type { ProjectSummaryProps } from '../projectDashboardTypes';
import { SidebarLayout } from '../../layouts';
import styles from './SBOMSummary.module.less';
import useSummaryActions from './useSummaryActions';
import Card from '../../generic/Card/Card';
import ModalStreamBuild from '../../modals/ModalStreamBuild';
import SBOMSummaryBuildProcess from './SBOMSummaryBuildProcess';
import SBOMAnalysisComponent from './SBOMAnalysis/SBOMAnalysisComponent';
import IssueDataView from './IssueDataView';
import RevisionActions from '../../../actions/RevisionActions';
import IssueScanDropdown from './IssueScanDropdown';
import { queryClient } from '../../../ReactQuery';
import { Icon, icons } from '../../generic';
import Flex from '../../generic/Flex';
import getRevisionStatus from '../shared/projectSummary/getRevisionStatus';
import Loader from '../../generic/Loader';

/**
 * Topline statistics and actions for SBOM projects
 */
const SBOMSummary: VoidFunctionComponent<ProjectSummaryProps> = ({
  project,
  revision,
  baseUrl,
  params,
  onProjectChange,
}) => {
  const {
    isRebuilding,
    onRebuild,
    rebuildBuildId,
    onShowNoDependenciesModal,
    onVisitAPIEndpoint,
    routerPush,
    onHideModalStreamBuild,
  } = useSummaryActions({ revision, params });
  const status = useMemo(() => getRevisionStatus(revision, project), [revision, project]);
  const isLoading = status === 'loading';
  const isBuildComplete = ['failing', 'analyzed'].includes(status);

  // very lightly altered from ../overview/ProjectSummary.tsx
  const onScanIssues = (e: MouseEvent<Button>) => {
    e.stopPropagation();
    const revisionLocator = revision?.data?.locator ?? '';

    RevisionActions.scan(revisionLocator);

    //  This tells React Query to refetch the revision data for this locator.
    const revisionQueryKey = ['revisions', revisionLocator];
    queryClient.invalidateQueries(revisionQueryKey);
  };
  const areThereScans = (revision?.data?.revisionScans?.length ?? 0) > 0;
  return (
    <div className={`container container-responsive ${styles.sbomSummary}`}>
      <SidebarLayout>
        <SidebarLayout.Main>
          <Card isFullWidth isContentFullHeight>
            <Card.Header>
              <div className={styles.header}>
                <div className={styles.headerStack}>
                  <span className={styles.summary}>Summary</span>
                  <span className={styles.revision}>Revision {revision.data?.loc?.revision}</span>
                </div>
                <div className={styles.buttons}>
                  <IssueScanDropdown revision={revision} routerPush={routerPush} />
                  {areThereScans && (
                    <div className="btn-group">
                      <Button type="button" className={styles.runNewScan} onClick={onScanIssues}>
                        <Icon icon={icons.reload} /> Run new scan
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Card.Header>
            {isLoading && (
              <Card.Content>
                <Loader />
              </Card.Content>
            )}
            {!isLoading && (
              <Card.Content>
                {!isBuildComplete && (
                  <>
                    <SBOMSummaryBuildProcess
                      projectId={params.project_id}
                      revisionId={params.revision_id}
                      revision={revision}
                      onProjectChange={onProjectChange}
                    />
                    <SBOMAnalysisComponent
                      isBuildInProgress
                      projectLocator={params.project_id}
                      revisionId={params.revision_id}
                      revisionBaseUrl={baseUrl}
                      onRebuild={onRebuild}
                      isSBOMAnalysisEnabled={
                        (project.data.sbomAnalysisEnabled && _.isInteger(project.data.sbomPolicyId))
                      }
                    />
                  </>
                )}
                {isBuildComplete && (
                  <Flex gap={24} direction="column">
                    <IssueDataView project={project} revision={revision} params={params} />
                    <SBOMAnalysisComponent
                      projectLocator={params.project_id}
                      revisionId={params.revision_id}
                      revisionBaseUrl={baseUrl}
                      onRebuild={onRebuild}
                      isSBOMAnalysisEnabled={
                        (project.data.sbomAnalysisEnabled && _.isInteger(project.data.sbomPolicyId))
                      }
                    />
                  </Flex>
                )}
              </Card.Content>
            )}
          </Card>
        </SidebarLayout.Main>
        <SidebarLayout.Sidebar>
          <ActionCards
            project={project}
            revision={revision}
            isScannedForIssues={false}
            revisionBaseUrl={baseUrl}
            isRebuilding={false}
            onRebuild={onRebuild}
            onShowNoDependenciesModal={onShowNoDependenciesModal}
            onVisitAPIEndpoint={onVisitAPIEndpoint}
            routerPush={routerPush}
          />
          <ProjectAffiliationContainer
            projectLocator={project.data.locator}
            labels={project.data.labels?.map((l) => l.label)}
          />
        </SidebarLayout.Sidebar>
      </SidebarLayout>
      {!project.data.require_mediated_dependencies && rebuildBuildId !== null && (
        <ModalStreamBuild
          key="rebuild-modal-stream-build"
          show={isRebuilding}
          buildId={rebuildBuildId}
          refId={params.reference}
          onHide={onHideModalStreamBuild}
          isProjectLocator
        />
      )}
    </div>
  );
};

export default SBOMSummary;
